import React, { useContext, useState } from 'react';
import { media } from 'context';
import { APP_BANNER_HEIGHT, HEADER_Z_INDEX, Icon, IconButton, Layout, SIDEBAR_PRIMARY_WIDTH, Text } from 'ds';
import { useBackNavigation } from 'hooks';
import { PANTRY_SUBSCRIPTION_PATH } from 'routes/paths';
import { selectProductSubscriptions } from 'store/Consumables/selectors';
import { selectUIState } from 'store/UI/selectors';
import { useAppSelector } from 'store/hooks';
import StickyHeader from 'ux/Layouts/Shared/StickyHeader';

interface ConsumablesOnboardingSetPageHeaderSectionProps {
  onCartClick: () => void;
  type: 'basic' | 'premium';
  onInfoButtonClick: () => void;
  setIsChangesNotSavedModalVisible: (isVisible: boolean) => void;
  hasChanges: boolean;
}

const ConsumablesOnboardingSetPageHeaderSection: React.FC<ConsumablesOnboardingSetPageHeaderSectionProps> = ({
  onCartClick,
  type,
  onInfoButtonClick,
  setIsChangesNotSavedModalVisible,
  hasChanges
}) => {
  const { appBannerType } = useAppSelector(selectUIState);
  const { isMobile } = useContext(media);
  const productSubscriptions = useAppSelector(selectProductSubscriptions);
  const handleBack = useBackNavigation(PANTRY_SUBSCRIPTION_PATH);
  const HEADER_HEIGHT = 96;
  const [isTransparentMode, setIsTransparentMode] = useState<boolean>(true);
  const [opacity, setOpacity] = useState<number>(0);

  const handleScrollY = (scrollY: number) => {
    if (scrollY > HEADER_HEIGHT) {
      setIsTransparentMode(false);
      setOpacity(1);
    } else {
      if (scrollY === 0) {
        setOpacity(0);
      } else {
        const pct_scrolled = scrollY / HEADER_HEIGHT;
        setOpacity(0.85 * pct_scrolled + 0.15);
      }
      setIsTransparentMode(true);
    }
  };

  return (
    <Layout
      direction="column"
      height={isMobile ? 238 : 304}
      width="100%"
      color="purple-gradient-transparent"
      paddingBottom={isMobile ? 36 : 80}
      paddingTop={isMobile ? 68 : 96}
    >
      <StickyHeader
        position="fixed"
        top={appBannerType ? APP_BANNER_HEIGHT : 0}
        zIndex={HEADER_Z_INDEX}
        shadow={false}
        backgroundColor={isTransparentMode ? 'transparent' : undefined}
        background={`rgba(255, 255, 255, ${opacity})`}
        transition="background 200ms"
        onScroll={scrollY => handleScrollY(scrollY)}
        height={isMobile ? 68 : 96}
        align="center"
        width={`calc(100% - ${isMobile ? 0 : SIDEBAR_PRIMARY_WIDTH}px)`}
      >
        <Layout
          {...(isMobile ? { paddingRight: 24, paddingLeft: 12 } : { paddingX: 40 })}
          direction="row"
          justify="space-between"
          align="center"
          width="100%"
        >
          <IconButton
            name={isMobile ? 'leftChevron' : 'leftArrow'}
            size="lg"
            type="noBackground"
            onClick={() => {
              if (hasChanges) {
                setIsChangesNotSavedModalVisible(true);
              } else {
                handleBack();
              }
            }}
          />
          <Layout direction="row" gap={16} height="fit-content">
            <IconButton name="info" size="lg" type="noBackground" iconColor="blue-700" onClick={onInfoButtonClick} />
            <Layout direction="row" align="center" gap={4}>
              <IconButton
                name="shoppingCart"
                size="lg"
                type="noBackground"
                iconColor="blue-700"
                onClick={onCartClick}
              />
              {productSubscriptions && productSubscriptions.length > 0 && (
                <Text size="body-md" color="blue-700" semibold>
                  {productSubscriptions?.length ?? 0}
                </Text>
              )}
            </Layout>
          </Layout>
        </Layout>
      </StickyHeader>
      <Layout
        width="100%"
        paddingX={isMobile ? 36 : 80}
        paddingTop={isMobile ? 24 : undefined}
        justify="center"
        align="center"
      >
        <Layout direction="column" align="center" gap={isMobile ? 8 : 12}>
          <Layout direction="row" gap={8}>
            <Icon name="sparkle" size="xs" color="purple-gradient" />
            <Text size={isMobile ? 'overline-md' : 'overline-lg'} color="purple-gradient">
              RESTOCKED LIKE MAGIC
            </Text>
            <Icon name="sparkle" size="xs" color="purple-gradient" />
          </Layout>
          <Text size={isMobile ? 'headline-lg' : 'display-sm'} color="purple-gradient">
            {type === 'basic' ? 'Basic set' : 'Premium set'}
          </Text>
          <Text size={isMobile ? 'body-sm' : 'body-md'} color="gray-700" align={isMobile ? 'center' : undefined}>
            {type === 'basic'
              ? 'Essential snacks & supplies your team loves — no frills, just fuel.'
              : 'A richer mix of snacks and supplies to keep your team energized and equipped.'}
          </Text>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default ConsumablesOnboardingSetPageHeaderSection;
